/**
 * The acts on an alert group — Assegna, Rinvia, Chiudi on the whole group or its narrowed set, in
 * ONE request (owner rulings DEC-101 and DEC-90, `docs/adr/9040`).
 *
 * `POST /foundations/exceptions/group-actions` takes the set as a FILTER, never as picked rows, so
 * the set stays reconstructible (`selected_filter`, reqs/07 §3.5 d), and each alert still records
 * its own event. The planner composes the act in a drawer, then confirms a sentence that says
 * exactly what will happen — «Assegni 9.812 alert a te» — and the count in that sentence is the
 * `expectedCount` sent. If the set changed in between, the platform refuses (409
 * `alert_group_changed`): the drawer says so, shows the new count and asks again; nothing was
 * written. On success the toast says what the platform answered, never what was asked:
 * «Assegnati 9.790 · 22 saltati (…)».
 *
 * The same capabilities as the single acts (ADR 9022, ADR 2102): assign, release and snooze are
 * `assign` on `alert`; closing is `dispose`. Each in the group's market.
 *
 * The reasons are the platform's (`GET /foundations/exceptions/reason-codes`): a snooze takes a
 * code whose `acts` include `snooze`, a closure one whose `dispositions` include the decision —
 * the vocabularies the single acts use. The snooze limit is the platform's
 * (`exception.snooze_max_days`); its refusal is shown, never pre-judged here.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useQueryClient } from '@tanstack/react-query';

import { useApiQuery } from '@/api';
import { alertReasonVocabulary, myWorkCounts, type AlertDecision } from '@/api/generated';
import { useCapability } from '@/auth/capabilities';
import { WhyNot } from '@/auth/capability-gate';
import { useAccessGrants } from '@/auth/grants';
import { Button, DatePicker, Drawer, Input, Select, StatusIndicator } from '@/primitives';
import {
  AssigneePicker, ChipGroup, GroupActControls, GroupActOutcome, groupSkipReasons, groupSkippedSentence, useGroupAct,
  type GroupActCopy,
} from '@/planning';

import {
  ALERT_GROUP_CHANGED, ALERT_GROUP_TOO_LARGE,
  type AlertGroupActionOp, type AlertGroupActionParams, type AlertGroupActionResult, type AlertGroupFilter,
} from '@/api/pending/alert-groups';
import { composeAlertGroupAction } from './alert-groups';
import { earliestSnoozeDay, reasonsForAct, snoozeDayIsFuture } from './alert-queue';

/** The three acts a planner chooses; releasing is the «nobody» choice of Assegna. */
export type GroupActKind = 'assign' | 'snooze' | 'dispose';
export const GROUP_ACT_KINDS: readonly GroupActKind[] = ['assign', 'snooze', 'dispose'];

const ACT_LABEL_KEY: Readonly<Record<GroupActKind, string>> = {
  assign: 'foundations.alertGroups.act.assign',
  snooze: 'foundations.alertGroups.act.snooze',
  dispose: 'foundations.alertGroups.act.dispose',
};

/** The decisions a group may be closed with — the single act's, `defer` excluded (ADR 2102). */
export const GROUP_DISPOSITIONS: readonly AlertDecision[] = ['accept', 'adjust', 'reject'];
const DISPOSITION_KEY: Readonly<Record<AlertDecision, string>> = {
  accept: 'foundations.alertGroups.disposition.accept',
  adjust: 'foundations.alertGroups.disposition.adjust',
  reject: 'foundations.alertGroups.disposition.reject',
};

/** Each answer's toast title, by the act the platform applied. */
const DONE_KEY: Readonly<Record<AlertGroupActionParams['body']['action'], string>> = {
  assign: 'foundations.alertGroups.done.assign',
  unassign: 'foundations.alertGroups.done.unassign',
  snooze: 'foundations.alertGroups.done.snooze',
  dispose: 'foundations.alertGroups.done.dispose',
};

/** The alert inbox's words for the grouped queue's act (ADR 9042) — its own catalogue's. */
export const ALERT_GROUP_ACT_COPY: GroupActCopy = {
  review: 'foundations.alertGroups.review',
  eachRecorded: 'foundations.alertGroups.eachRecorded',
  confirm: 'foundations.alertGroups.confirmAction',
  confirmAgain: 'foundations.alertGroups.confirmAgain',
  back: 'foundations.alertGroups.back',
  changedStated: 'foundations.alertGroups.changed.stated',
  changedRecount: 'foundations.alertGroups.changed.recount',
  tooLarge: 'foundations.alertGroups.tooLarge',
  failed: 'foundations.alertGroups.failed',
  skipReason: 'foundations.alertGroups.done.skipReason',
  skipped: 'foundations.alertGroups.done.skipped',
  noneSkipped: 'foundations.alertGroups.done.noneSkipped',
};

/** What an act takes: the filter, the count the planner sees, and the market it lands in. */
export interface GroupActTarget {
  readonly filter: AlertGroupFilter;
  /** The count shown beside the act — sent as `expectedCount`. `null` while it is being read. */
  readonly count: number | null;
  readonly country: string | null;
}

/** Never sent: the confirm button is disabled until the act is composed. */
const UNCOMPOSED: AlertGroupActionParams = { body: { action: 'unassign', filter: { ruleId: '' }, expectedCount: 0 } };

/** Re-read every alert read, every groups read and the badge — what an act changes. */
function useRefreshAlerts(): () => void {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({
      predicate: (query) => {
        const head = query.queryKey[0];
        return typeof head === 'string' && (head.includes('exceptions') || head === myWorkCounts.operationId);
      },
    });
  };
}

/**
 * The act buttons. `compact` (a row of the inbox, one line): a denied act is not drawn. Otherwise
 * the platform's reason stands in its place (ADR 9022).
 */
export function AlertGroupActButtons({ act, target, kinds = GROUP_ACT_KINDS, compact = false, blockedKey }: {
  readonly act: AlertGroupActionOp;
  readonly target: GroupActTarget;
  readonly kinds?: readonly GroupActKind[];
  readonly compact?: boolean;
  /** Why no act may be taken on the set as it stands (a filter the platform cannot carry). */
  readonly blockedKey?: string;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState<GroupActKind | null>(null);
  const where = { country: target.country };
  const assign = useCapability('alert', 'assign', where);
  const dispose = useCapability('alert', 'dispose', where);
  const verdict = (kind: GroupActKind) => (kind === 'dispose' ? dispose : assign);
  const empty = target.count === null || target.count === 0;

  return (
    <span className="flex flex-wrap items-center gap-2" data-group-acts="">
      {kinds.map((kind) => {
        const judged = verdict(kind);
        if (judged.state === 'denied') return compact ? null : <WhyNot key={kind} verdict={judged} />;
        return (
          <Button
            key={kind}
            size="sm"
            variant={compact ? 'ghost' : 'secondary'}
            data-group-act={kind}
            disabled={empty || blockedKey !== undefined}
            onClick={() => setOpen(kind)}
          >
            {t(ACT_LABEL_KEY[kind])}
          </Button>
        );
      })}
      {!compact && blockedKey !== undefined && <span className="text-caption text-ink-secondary">{t(blockedKey)}</span>}
      <Drawer open={open !== null} onOpenChange={(next) => { if (!next) setOpen(null); }} titleKey={open === null ? 'foundations.alertGroups.act.title' : ACT_LABEL_KEY[open]}>
        {open !== null && target.count !== null && (
          <GroupActForm act={act} kind={open} target={{ ...target, count: target.count }} onClose={() => setOpen(null)} />
        )}
      </Drawer>
    </span>
  );
}

type AssignTo = 'assign' | 'nobody';

/** The confirm sentence for a composed act — what will happen, with the count that is sent. */
export function confirmSentence(params: AlertGroupActionParams, me: string | null, labels: {
  readonly reason: string; readonly disposition: string;
  /** The assignee's name from the picker; `null`: none on record, or an id typed. */
  readonly person?: string | null;
}): { readonly key: string; readonly values: Record<string, string | number> } {
  const { body } = params;
  const count = body.expectedCount;
  switch (body.action) {
    case 'assign':
      return body.assignee === me
        ? { key: 'foundations.alertGroups.confirm.assignMe', values: { count } }
        : { key: 'foundations.alertGroups.confirm.assignPerson', values: { count, person: labels.person ?? body.assignee ?? '' } };
    case 'unassign':
      return { key: 'foundations.alertGroups.confirm.unassign', values: { count } };
    case 'snooze':
      return { key: 'foundations.alertGroups.confirm.snooze', values: { count, until: body.snoozedUntil ?? '' } };
    case 'dispose':
    default:
      return { key: 'foundations.alertGroups.confirm.dispose', values: { count, disposition: labels.disposition, reason: labels.reason } };
  }
}

function GroupActForm({ act, kind, target, onClose }: {
  readonly act: AlertGroupActionOp;
  readonly kind: GroupActKind;
  readonly target: GroupActTarget & { readonly count: number };
  readonly onClose: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { subject } = useAccessGrants();
  const refresh = useRefreshAlerts();
  const [now] = useState(() => Date.now());
  const vocabulary = useApiQuery(alertReasonVocabulary, {});

  const [assignTo, setAssignTo] = useState<AssignTo | null>(kind === 'assign' ? 'assign' : null);
  // «Assegna a me» is the first option of the picker, and chosen until the planner picks someone else.
  const [person, setPerson] = useState<{ readonly subject: string | null; readonly name: string | null }>({ subject, name: null });
  const [until, setUntil] = useState<string | null>(null);
  const [disposition, setDisposition] = useState<AlertDecision | null>(null);
  const [reasonCode, setReasonCode] = useState<string | null>(null);
  const [commentary, setCommentary] = useState('');

  const action = kind === 'assign' ? (assignTo === 'nobody' ? 'unassign' : 'assign') : kind;
  const assignee = assignTo === 'assign' ? person.subject : null;
  // The grouped queue's lifecycle (ADR 9042): the count confirmed is the `expectedCount` sent.
  const state = useGroupAct<AlertGroupActionParams, AlertGroupActionResult>({
    operation: act,
    count: target.count,
    compose: (expectedCount) => composeAlertGroupAction({
      action, filter: target.filter, expectedCount, assignee, snoozedUntil: until, disposition, reasonCode, commentary,
    }),
    uncomposed: UNCOMPOSED,
    problems: { changed: ALERT_GROUP_CHANGED, tooLarge: ALERT_GROUP_TOO_LARGE },
    refresh,
    done: () => ({ titleKey: DONE_KEY[action] }),
    copy: ALERT_GROUP_ACT_COPY,
  });
  const { params, setConfirming } = state;

  const options = vocabulary.data ?? [];
  const offered = kind === 'snooze'
    ? reasonsForAct(options, 'snooze')
    : kind === 'dispose' && disposition !== null ? options.filter((option) => option.dispositions.includes(disposition)) : [];
  const early = until !== null && !snoozeDayIsFuture(until, now);
  const reasonLabel = options.find((option) => option.code === reasonCode)?.label.text ?? reasonCode ?? '';
  const sentence = params === null ? null : confirmSentence(params, subject, {
    reason: reasonLabel, disposition: disposition === null ? '' : t(DISPOSITION_KEY[disposition]), person: person.name,
  });

  if (state.outcome !== null) {
    return <GroupActOutcome answer={state.outcome} titleKey={DONE_KEY[action]} copy={ALERT_GROUP_ACT_COPY} onClose={onClose} />;
  }

  return (
    <div className="flex flex-col gap-4" data-group-act-form={kind}>
      {kind === 'assign' && (
        <div className="flex flex-col gap-2">
          <ChipGroup
            legendKey="foundations.alertGroups.assign.to"
            options={[
              { value: 'assign', label: t('foundations.alertGroups.assign.person') },
              { value: 'nobody', label: t('foundations.alertGroups.assign.nobody') },
            ]}
            value={assignTo}
            onChange={(value) => { setAssignTo(value as AssignTo); setConfirming(false); }}
          />
          {assignTo === 'assign' && (
            <AssigneePicker
              country={target.country}
              ruleId={target.filter.ruleId}
              me={subject}
              value={person.subject}
              onChange={(chosen, name) => { setPerson({ subject: chosen, name }); setConfirming(false); }}
            />
          )}
        </div>
      )}

      {kind === 'snooze' && (
        <div className="grid max-w-md gap-2">
          <p className="text-caption text-ink-secondary">{t('foundations.alertGroups.snooze.explain', { earliest: earliestSnoozeDay(now) })}</p>
          <DatePicker
            labelKey="foundations.alerts.snooze.until"
            {...(early ? { errorKey: 'foundations.alerts.snooze.notFuture' } : {})}
            {...(until === null ? {} : { value: until })}
            onValueChange={(value) => { setUntil(value); setConfirming(false); }}
          />
        </div>
      )}

      {kind === 'dispose' && (
        <ChipGroup
          legendKey="foundations.alertGroups.dispose.decision"
          options={GROUP_DISPOSITIONS.map((code) => ({ value: code, label: t(DISPOSITION_KEY[code]) }))}
          value={disposition}
          onChange={(value) => { setDisposition(value as AlertDecision); setReasonCode(null); setConfirming(false); }}
        />
      )}

      {(kind === 'snooze' || kind === 'dispose') && (
        <div className="max-w-md">
          {kind === 'dispose' && disposition === null ? (
            <p className="text-caption text-ink-secondary">{t('foundations.alertGroups.dispose.reasonNeedsDecision')}</p>
          ) : vocabulary.isPending ? (
            <p className="text-caption text-ink-secondary">{t('state.loading')}</p>
          ) : offered.length === 0 ? (
            <StatusIndicator level="serious" labelKey={vocabulary.isError ? 'foundations.alerts.reasons.failed' : 'foundations.alertGroups.noReason'} />
          ) : (
            <Select
              labelKey="foundations.alertGroups.reason"
              placeholderKey="foundations.alerts.reasons.placeholder"
              options={offered.map((option) => ({ value: option.code, label: option.label.text }))}
              {...(reasonCode === null ? {} : { value: reasonCode })}
              onValueChange={(value) => { setReasonCode(value); setConfirming(false); }}
            />
          )}
        </div>
      )}

      <div className="max-w-md">
        <Input labelKey="foundations.alerts.commentary" value={commentary} onChange={(event) => setCommentary(event.target.value)} />
      </div>

      <GroupActControls state={state} copy={ALERT_GROUP_ACT_COPY} sentence={sentence} destructive={kind === 'dispose'} reviewDisabled={early} />
    </div>
  );
}

/** The skipped alerts' reasons, the platform's words verbatim with their counts, as one list. */
export function skipReasons(
  result: AlertGroupActionResult, t: TFunction, language: string,
): string {
  return groupSkipReasons(result, t, language, ALERT_GROUP_ACT_COPY);
}

/** The toast's second line: what was skipped and why, or that nothing was. */
export function skippedSentence(
  result: AlertGroupActionResult, t: TFunction, language: string,
): { readonly descriptionKey: string; readonly descriptionValues: Record<string, unknown> } {
  return groupSkippedSentence(result, t, language, ALERT_GROUP_ACT_COPY);
}
